import React from 'react';
import { useTranslation } from 'react-i18next';
import ResultContainer from '../ResultContainer';
import '../../styles/DemoPage.css';

const DemoPage = ({ forceRenderKey, ticker, setTicker, loading, tickerError, tickerResult, handleSubmit }) => {
  const { t } = useTranslation();

  return (
    <div className="demo-section" key={`demo-${forceRenderKey}`}>
      <div className="demo-container">
        <div className="futuristic-card enhanced-demo-card">
          <div className="card-glow"></div>
          <div className="card-content">
            <div className="demo-header">
              <div className="title-container">
                <h2 className="futuristic-title enhanced-title">
                  <span className="title-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M12 16v-4"></path>
                      <path d="M12 8h.01"></path>
                    </svg>
                  </span>
                  {t('demo.title')}
                  <span className="title-badge enhanced-badge">
                    <span className="badge-glow"></span>
                    v3.0.1
                  </span>
                </h2>
                <div className="version-info">
                  <div className="version-details">
                    <span className="version-label">Latest Release</span>
                    <span className="version-date">March 2025</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="ai-status-container enhanced-status">
              <div className="ai-status-panel enhanced-panel">
                <div className="status-header">
                  <div className="status-indicator active enhanced-indicator">
                    <span className="pulse-dot"></span>
                    <span className="status-text">{t('demo.systemOnline')}</span>
                    <div className="status-glow"></div>
                  </div>
                  <div className="system-time enhanced-time">
                    <span className="time-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </span>
                    {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
                <div className="status-metrics enhanced-metrics">
                  <div className="metric-item enhanced-metric systems-metric">
                    <div className="metric-header">
                      <div className="metric-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="9" y1="3" x2="9" y2="21"></line>
                          <line x1="15" y1="3" x2="15" y2="21"></line>
                          <line x1="3" y1="9" x2="21" y2="9"></line>
                          <line x1="3" y1="15" x2="21" y2="15"></line>
                        </svg>
                      </div>
                      <div className="metric-value enhanced-value">7</div>
                    </div>
                    <div className="metric-label">{t('demo.activeSystems')}</div>
                    <div className="systems-grid">
                      <div className="system-item">
                        <div className="system-dot prag-dot"></div>
                        <span className="system-name">Prag</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot hyper-dot"></div>
                        <span className="system-name">Hyper</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot riemann-dot"></div>
                        <span className="system-name">Riemann</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot euler-dot"></div>
                        <span className="system-name">Euler</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot tachyon-dot"></div>
                        <span className="system-name">Tachyon</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot aether-dot"></div>
                        <span className="system-name">Aether</span>
                      </div>
                      <div className="system-item">
                        <div className="system-dot sigma-dot"></div>
                        <span className="system-name">Sigma</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="ticker-input-panel enhanced-input-panel">
                <div className="input-label enhanced-label">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                  </svg>
                  <span>{t('demo.stockAnalysis')}</span>
                </div>
                <form onSubmit={handleSubmit} className="futuristic-form enhanced-form">
                  <div className="input-wrapper enhanced-wrapper">
                    <input
                      type="text"
                      value={ticker}
                      onChange={(e) => setTicker(e.target.value.toUpperCase())}
                      placeholder={t('demo.tickerPlaceholder')}
                      aria-label={t('demo.stockTicker')}
                      className="futuristic-input enhanced-input"
                    />
                    <div className="input-glow"></div>
                  </div>
                  <button
                    type="submit"
                    className="futuristic-button enhanced-button"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="button-icon loading enhanced-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"></path>
                          </svg>
                        </span>
                        {t('demo.processing')}
                      </>
                    ) : (
                      <>
                        <span className="button-icon enhanced-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                          </svg>
                        </span>
                        {t('demo.analyze')}
                      </>
                    )}
                  </button>
                </form>
                <div className="note enhanced-note">{t('demo.enterTickerNote')}</div>
              </div>
            </div>
            
            {loading && (
              <div className="futuristic-loading enhanced-loading">
                <div className="loading-animation enhanced-animation">
                  <div className="futuristic-spinner">
                    <div className="spinner-core">
                      <div className="core-pulse"></div>
                    </div>
                    <div className="spinner-orbit orbit-1">
                      <div className="orbit-dot"></div>
                    </div>
                    <div className="spinner-orbit orbit-2">
                      <div className="orbit-dot"></div>
                    </div>
                    <div className="spinner-orbit orbit-3">
                      <div className="orbit-dot"></div>
                    </div>
                  </div>
                </div>
                <div className="loading-text enhanced-text">
                  <div className="loading-title">
                    <span className="title-text">{t('demo.runningAnalysis')}</span>
                  </div>
                  <div className="loading-details enhanced-details">
                    <div className="loading-progress enhanced-progress">
                      <div className="progress-line enhanced-line"></div>
                      <div className="processing-text">
                        <span className="processing-label">{t('demo.processingSignals')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tickerError && (
              <div className="error-message futuristic-error enhanced-error">
                <span className="error-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </span>
                <p>{tickerError}</p>
              </div>
            )}

            {tickerResult && <ResultContainer result={tickerResult} key={`result-${forceRenderKey}`} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
